import type { FC } from 'react';
import React, { Fragment, useEffect, useRef, useState } from 'react';

import { IconExternal, TrashBinIcon } from '../../../assets/icons/icons';
import { IconSupplierPortal, IconWildberries } from '../../../assets/icons/marketplaces';
import {
    useDeleteMarketplaceMutation,
    useDeleteSourcesMutation,
    useDeleteSupplierPortalMutation,
    useGetProductAnalogsQuery,
    useGetProductMarketplacesQuery,
    useGetSupplierPortalQuery,
    useStartAnalogsLogisticsMutation,
} from '../../../entities/project/api/projectApi';
import { FINISHED_STATUSES } from '../../../entities/project/model/searchState';
import type { Position } from '../../../types/project';
import type { SupplierOffer } from '../../../types/results';
import { Switch } from '../../ui/Switch/Switch';
import { useToast } from '../../ui/Toast/ToastProvider';
import { cx } from '../../../utils/cx';
import { formatRub, pluralWithCount, round2 } from '../../../utils/format';
import styles from './PositionDetails.module.scss';
import { Tooltip } from '../../ui/Tooltip/Tooltip';
import { CatalogThumb } from '../../ui/CatalogThumb/CatalogThumb';
import { Waiting } from '../../ui/Waiting/Waiting';
import { AnalogCompareRow, MatchToggle, openRowClass } from '../AnalogCompare/AnalogCompare';
import { isFallbackMatch } from '../../../entities/project/model/mappers';
import { useIsAdmin } from '../../../hooks/useIsAdmin';
import { useManualOffers } from '../../../hooks/useManualOffers';
import { ManualOffers } from '../ManualOffers/ManualOffers';
import { ConfirmDeleteModal } from '../ConfirmDeleteModal/ConfirmDeleteModal';
import { OFFER_COL } from './offersColumns';

const MARKET_POLL_MS = 4000;

const ANALOG_POLL_MS = 7000;
const ANALOG_SLOW_POLL_MS = 30_000;
const ANALOG_TIMEOUT_MS = 5 * 60_000;
type ZoneState = 'off' | 'pending' | 'calc' | 'on' | 'hidden';

export type AnalogsLogistics = 'skipped' | 'pending' | 'processing' | 'done';

const zoneVisible = (zone: ZoneState): boolean => zone !== 'off' && zone !== 'hidden';

interface PositionDetailsProps {
    reportId: string;
    position: Position;
    address: string;
    onAnalogsLogistics?: (status: AnalogsLogistics) => void;
    onOfferDeleted?: () => void;
}

type RefSource = 'wb' | 'portal';

const REF_NAMES: Record<RefSource, string> = {
    wb: 'Wildberries',
    portal: 'Портал поставщиков',
};

type DeleteTarget =
    | { kind: 'offer'; offer: SupplierOffer }
    | { kind: 'ref'; source: RefSource };

const deleteCopy = (
    target: DeleteTarget,
): { title: string; name: string; items: string[]; warnText: string } =>
    target.kind === 'offer'
        ? {
            title: 'Удалить предложение?',
            name: target.offer.name,
            items: [
                'предложение исчезнет из позиции и не попадёт в отчёт;',
                'цены позиции будут пересчитаны автоматически.',
            ],
            warnText: 'Повторный поиск по позиции не вернёт удалённые предложения.',
        }
        : {
            title: 'Удалить справочную цену?',
            name: `Карточка «${REF_NAMES[target.source]}»`,
            items: [
                'карточка исчезнет из позиции и не попадёт в отчёт;',
                'цены позиции будут пересчитаны автоматически.',
            ],
            warnText: 'При повторном поиске по позиции эта площадка может появиться снова.',
        };

interface ZoneCellProps {
    zone: ZoneState;
    value: number;
    best?: boolean;
    spinner?: boolean;
}

const ZoneCell: FC<ZoneCellProps> = ({ zone, value, best, spinner }) => {
    const className = cx(styles.num, styles.zone, best && styles.zoneBest);

    if (!zoneVisible(zone)) return <td className={cx(styles.zone, styles.zoneOff)} />;
    if (zone === 'pending') return <td className={className}><span className={styles.dash}>—</span></td>;
    if (zone === 'calc') {
        return (
            <td className={className}>
                {spinner ? <Waiting text="Считаем…" /> : <span className={styles.calc}>считаем…</span>}
            </td>
        );
    }

    return <td className={className}>{value > 0 ? formatRub(value) : <span className={styles.dash}>—</span>}</td>;
};

/* ─── таблица предложений ─── */

interface OffersTableProps {
    offers: SupplierOffer[];
    qty: number;
    badge?: string;
    withMatch?: boolean;
    zone: ZoneState;
    calcSpinner?: boolean;
    deliveryOf: (offer: SupplierOffer) => number;
    onDelete?: (offer: SupplierOffer) => void;
}

const matchTone = (pct: number): 'good' | 'mid' | 'bad' =>
    pct >= 80 ? 'good' : pct > 50 ? 'mid' : 'bad';

const OffersTable: FC<OffersTableProps> = ({
    offers,
    qty,
    badge = 'лучшая цена',
    withMatch = false,
    zone,
    calcSpinner = false,
    deliveryOf,
    onDelete,
}) => {
    const show = zoneVisible(zone);

    /* одновременно раскрыто одно сравнение */
    const [openId, setOpenId] = useState<string | null>(null);
    const toggleCompare = (id: string): void => setOpenId((current) => (current === id ? null : id));
    const colSpan = withMatch ? 8 : 7;

    const totals = offers.map((offer) => {
        const delivery = deliveryOf(offer);
        return delivery > 0 ? offer.price * qty + delivery : 0;
    });
    const bestTotal = zone === 'on' ? Math.min(...totals.filter((t) => t > 0), Infinity) : Infinity;

    return (
        <table className={styles.offers}>
            {/* ширины общие с ManualOffers — см. offersColumns.ts */}
            <colgroup>
                <col />
                {withMatch && <col style={{ width: `${OFFER_COL.match}px` }} />}
                <col style={{ width: `${OFFER_COL.price}px` }} />
                <col style={{ width: `${OFFER_COL.qty}px` }} />
                <col style={{ width: `${OFFER_COL.cost}px` }} />
                <col style={{ width: show ? `${OFFER_COL.logistics}px` : '0' }} />
                <col style={{ width: show ? `${OFFER_COL.total}px` : '0' }} />
                <col style={{ width: `${OFFER_COL.actions}px` }} />
            </colgroup>

            <thead>
                <tr>
                    <th>Продавец</th>
                    {withMatch && <th>Соответ.</th>}
                    <th>Цена за ед.</th>
                    <th>Кол-во</th>
                    <th>Стоимость</th>
                    <th className={cx(styles.num, styles.zone, !show && styles.zoneOff)}>
                        {show && 'Логистика'}
                    </th>
                    <th className={cx(styles.num, styles.zone, !show && styles.zoneOff)}>
                        {show && 'Итого с доставкой'}
                    </th>
                    <th />
                </tr>
            </thead>

            <tbody>
                {offers.map((offer, index) => {
                    const delivery = deliveryOf(offer);
                    const total = totals[index];
                    const hasSpecs = withMatch && Boolean(offer.specs?.length);
                    const isOpen = hasSpecs && openId === offer.id;
                    const compareId = `cmp-${index}-${offer.id}`.replace(/[^\w-]/g, '_');
                    const fallback = withMatch && isFallbackMatch(offer.matchPct);

                    return (
                        <Fragment key={offer.id}>
                            <tr className={cx(index === 0 && styles.best, isOpen && openRowClass, fallback && styles.fallback)}>
                                <td>
                                    <span className={styles.seller}>
                                        <CatalogThumb src={offer.pic} />
                                        <span className={styles.sellerText}>
                                            <Tooltip text={offer.name}>
                                                <b>{offer.name}</b>
                                            </Tooltip>
                                            <Tooltip text={offer.name}>
                                                <small>
                                                    {offer.host || offer.name}
                                                    {index === 0 && badge && <span className={styles.badge}>{badge}</span>}
                                                    {fallback && <span className={styles.fallbackBadge}>добор</span>}
                                                </small>
                                            </Tooltip>
                                        </span>
                                    </span>
                                </td>
                                {withMatch && (
                                    <td className={styles.num}>
                                        {offer.matchPct != null && hasSpecs ? (
                                            <MatchToggle
                                                pct={offer.matchPct}
                                                tone={matchTone(offer.matchPct)}
                                                className={styles.match}
                                                open={isOpen}
                                                controls={compareId}
                                                onToggle={() => toggleCompare(offer.id)}
                                            />
                                        ) : offer.matchPct != null ? (
                                            <span className={styles.match} data-tone={matchTone(offer.matchPct)}>
                                                {offer.matchPct}%
                                            </span>
                                        ) : (
                                            '—'
                                        )}
                                    </td>
                                )}
                                <td className={styles.num}>{formatRub(offer.price)}</td>
                                <td className={styles.num}>{qty} шт</td>
                                <td className={styles.sum}>{formatRub(offer.price * qty)}</td>
                                <ZoneCell zone={zone} value={delivery} spinner={calcSpinner} />
                                <ZoneCell
                                    zone={zone}
                                    value={total}
                                    best={total > 0 && total === bestTotal}
                                    spinner={calcSpinner}
                                />
                                <td className={styles.actsCell}>
                                    <span className={styles.acts}>
                                        <a
                                            className={styles.go}
                                            href={offer.url}
                                            target="_blank"
                                            rel="noreferrer noopener"
                                            aria-label={`Открыть «${offer.name}» на сайте продавца`}
                                        >
                                            <IconExternal />
                                        </a>
                                        {onDelete && offer.url && (
                                            <button
                                                type="button"
                                                className={cx(styles.iconBtn, styles.danger)}
                                                aria-label={`Удалить «${offer.name}»`}
                                                onClick={() => onDelete(offer)}
                                            >
                                                <TrashBinIcon />
                                            </button>
                                        )}
                                    </span>
                                </td>
                            </tr>
                            {isOpen && offer.specs && (
                                <AnalogCompareRow
                                    id={compareId}
                                    colSpan={colSpan}
                                    specs={offer.specs}
                                    summary={offer.specsSummary}
                                    analogUrl={offer.url}
                                />
                            )}
                        </Fragment>
                    );
                })}
            </tbody>
        </table>
    );
};

/* ─── справочные цены ─── */

interface RefCardProps {
    logo: 'pp' | 'wb';
    name: string;
    productName: string;
    qty: number;
    price: number | null;
    url?: string;
    emptyText: string;
    onDelete?: () => void;
}

const RefCard: FC<RefCardProps> = ({ logo, name, productName, qty, price, url, emptyText, onDelete }) => {
    const top = (
        <div className={styles.refTop}>
            <span className={styles.logo}>
                {logo === 'wb' ? <IconWildberries /> : <IconSupplierPortal />}
            </span>
            <span className={styles.refGrow}>
                <b>{name}</b>
                <small>справочная цена</small>
            </span>
            <span className={styles.refActions}>
                {price !== null && url && (
                    <a
                        className={styles.go}
                        href={url}
                        target="_blank"
                        rel="noreferrer noopener"
                        aria-label={`Открыть на площадке «${name}»`}
                    >
                        <IconExternal />
                    </a>
                )}
                {onDelete && (
                    <button
                        type="button"
                        className={cx(styles.iconBtn, styles.danger)}
                        aria-label={`Удалить справочную цену «${name}»`}
                        onClick={onDelete}
                    >
                        <TrashBinIcon />
                    </button>
                )}
            </span>
        </div>
    );

    if (price === null) {
        return (
            <div className={cx(styles.refCard, styles.none)}>
                {top}
                <div className={styles.emptyVal}>{emptyText}</div>
            </div>
        );
    }

    return (
        <div className={styles.refCard}>
            {top}

            <div className={styles.refMid}>
                <span className={styles.refInfo}>
                    <span className={styles.refProd}>{productName}</span>
                    <span className={styles.refPrice}>
                        <b>{formatRub(price)}</b>
                        <span>за шт</span>
                    </span>
                </span>
            </div>

            <div className={styles.refBot}>
                {qty} шт
                <span className={styles.refSum}>{formatRub(price * qty)}</span>
            </div>
        </div>
    );
};

/* ─── детали позиции ─── */

export const PositionDetails: FC<PositionDetailsProps> = ({
    position,
    reportId,
    address,
    onAnalogsLogistics,
    onOfferDeleted,
}) => {
    const notify = useToast();
    const [confirming, setConfirming] = useState(false);

    /* ручные источники по ссылке — только admin / super_admin */
    const admin = useIsAdmin();
    const manualSources = useManualOffers(reportId, position.id);

    const allOffers = position.offers ?? [];
    /* админу ручные — отдельным блоком с формой, остальным — в общей таблице */
    const offers = allOffers.filter((offer) => !admin || !offer.isManual).sort((a, b) => a.price - b.price);
    const manualItems = admin ? allOffers.filter((offer) => offer.isManual) : [];

    const addManualSource: typeof manualSources.add = async (draft) => {
        const failure = await manualSources.add(draft);
        if (!failure) notify('Источник добавлен');
        return failure;
    };

    const avgPrice = round2(position.avgFoundPrice ?? 0);
    const deliverySum = position.logistics ? round2(position.deliveryCost ?? 0) : 0;
    const goodsSum = round2(avgPrice * position.qty);

    const searching = !FINISHED_STATUSES.has((position.searchStatus ?? '').toLowerCase());

    const mainZone: ZoneState = !position.logistics
        ? 'off'
        : searching
            ? 'calc'
            : deliverySum > 0
                ? 'on'
                : 'pending';

    const [marketPending, setMarketPending] = useState(true);

    const { data: market, isError: marketFailed } = useGetProductMarketplacesQuery(position.id, {
        skip: searching || !position.id,
        pollingInterval: marketPending ? MARKET_POLL_MS : 0,
    });

    useEffect(() => {
        if (market) setMarketPending(market.isSearching);
        if (marketFailed) setMarketPending(false);
    }, [market, marketFailed]);

    const [portalPending, setPortalPending] = useState(true);

    const { data: portal, isError: portalFailed } = useGetSupplierPortalQuery(position.id, {
        skip: searching || !position.id,
        pollingInterval: portalPending ? MARKET_POLL_MS : 0,
    });

    useEffect(() => {
        if (portal) setPortalPending(portal.isSearching);
        if (portalFailed) setPortalPending(false);
    }, [portal, portalFailed]);

    const [analogsInterval, setAnalogsInterval] = useState(ANALOG_POLL_MS);

    const { data: analogs, isError: analogsFailed } = useGetProductAnalogsQuery(position.id, {
        skip: searching || !position.id || !position.analogs,
        pollingInterval: analogsInterval,
    });

    const analogsStage = !analogs ? 'idle' : analogs.isSearching ? 'search' : analogs.logistics;

    useEffect(() => {
        const busy = analogsStage === 'idle' || analogsStage === 'search' || analogsStage === 'processing';

        if (analogsFailed || !busy) {
            setAnalogsInterval(0);
            return undefined;
        }

        setAnalogsInterval(ANALOG_POLL_MS);
        const timer = window.setTimeout(() => setAnalogsInterval(ANALOG_SLOW_POLL_MS), ANALOG_TIMEOUT_MS);
        return () => window.clearTimeout(timer);
    }, [analogsStage, analogsFailed, position.id]);

    const analogsSearching = analogsStage === 'idle' || analogsStage === 'search';
    const analogsSlow = analogsInterval === ANALOG_SLOW_POLL_MS;

    const analogOffers: SupplierOffer[] = (analogs?.items ?? [])
        .map((item) => ({
            id: item.url || item.title,
            name: item.title,
            host: item.host,
            price: item.price,
            url: item.url,
            pic: item.pic,
            matchPct: item.matchPct,
            specs: item.specs,
            specsSummary: item.specsSummary,
            deliveryCost: item.delivery?.avg ?? 0,
        }))
        .sort((a, b) => a.price - b.price);

    const analogAvg = analogOffers.length
        ? round2(analogOffers.reduce((sum, item) => sum + item.price, 0) / analogOffers.length)
        : 0;

    const [deleteTarget, setDeleteTarget] = useState<DeleteTarget | null>(null);
    const [deleteSources, { isLoading: sourcesDeleting }] = useDeleteSourcesMutation();
    const [deleteMarketplace, { isLoading: marketplaceDeleting }] = useDeleteMarketplaceMutation();
    const [deleteSupplierPortal, { isLoading: portalDeleting }] = useDeleteSupplierPortalMutation();

    const deleting = sourcesDeleting || marketplaceDeleting || portalDeleting;
    const canDelete = !searching;

    const askDelete = (offer: SupplierOffer): void => setDeleteTarget({ kind: 'offer', offer });
    const askDeleteRef = (source: RefSource): void => setDeleteTarget({ kind: 'ref', source });

    const cancelDelete = (): void => {
        if (!deleting) setDeleteTarget(null);
    };

    const confirmDelete = async (): Promise<void> => {
        if (!deleteTarget) return;

        try {
            if (deleteTarget.kind === 'offer') {
                if (!deleteTarget.offer.url) return;
                await deleteSources({ reportId, productId: position.id, urls: [deleteTarget.offer.url] }).unwrap();
                notify('Предложение удалено, цены позиции пересчитаны');
                /* позиция стала «изменённой» — в строке появится «Перезапустить поиск»,
                   чтобы добрать новые ссылки взамен удалённой */
                onOfferDeleted?.();
            } else if (deleteTarget.source === 'wb') {
                await deleteMarketplace({ productId: position.id, marketplace: 'wildberries' }).unwrap();
                notify('Справочная цена удалена');
            } else {
                await deleteSupplierPortal(position.id).unwrap();
                notify('Справочная цена удалена');
            }
            setDeleteTarget(null);
        } catch (error) {
            const err = error as { data?: { message?: string } };
            notify(err.data?.message ?? 'Не удалось удалить');
        }
    };

    /* ─── запуск логистики по аналогам ─── */

    const [startAnalogsLogistics] = useStartAnalogsLogisticsMutation();

    const [analogsHidden, setAnalogsHidden] = useState(false);
    const [analogsStarting, setAnalogsStarting] = useState(false);

    const analogsLogistics: AnalogsLogistics = analogs?.logistics ?? 'pending';
    const analogsPriced = analogOffers.some((item) => item.price > 0);

    const analogsPricedCount = analogOffers.filter((item) => item.price > 0).length;

    const reportRef = useRef(onAnalogsLogistics);

    useEffect(() => {
        reportRef.current = onAnalogsLogistics;
    });

    useEffect(() => {
        reportRef.current?.(analogsLogistics);
    }, [analogsLogistics]);

    const analogZone: ZoneState =
        analogsLogistics === 'processing' || analogsStarting
            ? 'calc'
            : analogsLogistics === 'done'
                ? analogsHidden ? 'hidden' : 'on'
                : mainZone === 'on' || mainZone === 'pending'
                    ? 'pending'
                    : 'off';

    const analogWithDelivery = analogOffers.filter((item) => (item.deliveryCost ?? 0) > 0);
    const analogAvgWithDelivery = analogWithDelivery.length
        ? round2(
            analogWithDelivery.reduce(
                (sum, item) => sum + round2(item.price) * position.qty + (item.deliveryCost ?? 0),
                0,
            ) / analogWithDelivery.length,
        )
        : 0;

    const startLogistics = async (): Promise<void> => {
        setConfirming(false);
        setAnalogsStarting(true);
        try {
            const result = await startAnalogsLogistics(position.id).unwrap();
            setAnalogsHidden(false);
            notify(`Считаем ${pluralWithCount(result.toCalculate, 'аналог', 'аналога', 'аналогов')}`);
        } catch (error) {
            const err = error as { status?: number; data?: { message?: string } };
            if (err.status !== 409) notify(err.data?.message ?? 'Не удалось запустить расчёт логистики');
        } finally {
            setAnalogsStarting(false);
        }
    };

    const toggleAnalogsLogistics = (): void => {
        if (analogZone === 'calc') return;
        if (analogsLogistics === 'done') {
            setAnalogsHidden((hidden) => !hidden);
            return;
        }
        if (!analogsPriced) {
            notify('Ни у одного аналога нет цены — везти нечего');
            return;
        }
        setConfirming(true);
    };

    const analogToggleTitle =
        analogZone === 'hidden'
            ? 'Расчёт сохранён. Включите, чтобы снова показать колонки логистики'
            : analogZone === 'on'
                ? 'Выключить — колонки логистики скроются, расчёт сохранится'
                : analogZone === 'calc'
                    ? 'Считаем…'
                    : !analogsPriced
                        ? 'Ни у одного аналога нет цены'
                        : 'Рассчитать логистику для аналогов';

    const analogToggleState = { off: 'выкл', pending: 'выкл', calc: 'вкл', on: 'вкл', hidden: 'выкл' }[analogZone];

    const mainDeliveryOf = (): number => deliverySum;

    const wildberries = market?.items.find((item) => item.key === 'wildberries');
    const wbRemoved = Boolean(market?.wildberriesRemoved);
    const portalRemoved = Boolean(portal?.removed);

    const marketLoading =
        searching ||
        (Boolean(market?.isSearching) && !marketFailed) ||
        (Boolean(portal?.isSearching) && !portalFailed);

    const showAnalogsBlock = position.analogs && analogsLogistics !== 'skipped';

    return (
        <div className={styles.details}>
            <div className={styles.left}>
                <section className={styles.blk}>
                    <header className={styles.main}>
                        <span className={styles.cap}>Основной поиск</span>
                        {!searching && (
                            <span className={styles.note}>
                                {offers.length ? `${offers.length} предложений` : 'предложений нет'}
                                {avgPrice ? ` · средняя за единицу ${formatRub(avgPrice)}` : ''}
                            </span>
                        )}
                    </header>

                    {searching ? (
                        <Waiting text="Ищем предложения…" />
                    ) : offers.length ? (
                        <>
                            <div className={styles.product}>
                                <CatalogThumb src={position.pic} large />
                                <span>
                                    <b>{position.name}</b>
                                    {position.spec && <small>{position.spec}</small>}
                                </span>
                            </div>
                            <OffersTable
                                offers={offers}
                                qty={position.qty}
                                zone={mainZone}
                                deliveryOf={mainDeliveryOf}
                                onDelete={canDelete ? askDelete : undefined}
                            />
                        </>
                    ) : (
                        <div className={styles.emptyBox}>
                            <p className={styles.empty}>
                                В магазинах предложений не нашлось.
                                {position.priceTo
                                    ? ` Возможно, найденное дороже ${formatRub(position.priceTo)} — попробуйте снять ограничение по цене.`
                                    : ' Уточните наименование позиции.'}
                            </p>

                            {avgPrice > 0 && (
                                <p className={styles.avgHint}>
                                    Средняя цена по найденному: <b>{formatRub(avgPrice)}</b>
                                </p>
                            )}
                        </div>
                    )}

                    {/* ручные предложения по ссылке — колонки совпадают с таблицей выше */}
                    {admin && !searching && (
                        <ManualOffers
                            draftKey={position.id}
                            offers={manualItems}
                            qty={position.qty}
                            showZone={zoneVisible(mainZone)}
                            deliveryOf={mainZone === 'on' ? mainDeliveryOf : () => 0}
                            withHeader={offers.length === 0}
                            onAdd={addManualSource}
                            onDelete={askDelete}
                        />
                    )}
                </section>

                {/* ═══ аналоги ═══ */}
                <section className={styles.blk}>
                    <header className={position.analogs ? styles.an : styles.off}>
                        <span className={styles.cap}>Аналоги</span>

                        {showAnalogsBlock && analogs && !analogsSearching && analogOffers.length > 0 && (
                            confirming ? (
                                <span className={styles.analogConfirm}>
                                    <span className={styles.analogConfirmText}>
                                        Рассчитать логистику для {pluralWithCount(analogsPricedCount, 'аналога', 'аналогов', 'аналогов')}?
                                    </span>
                                    <button
                                        type="button"
                                        className={cx(styles.analogConfirmBtn, styles.analogConfirmYes)}
                                        onClick={() => void startLogistics()}
                                        disabled={analogsStarting}
                                    >
                                        {analogsStarting ? 'Запускаем…' : 'Да'}
                                    </button>
                                    <button
                                        type="button"
                                        className={styles.analogConfirmBtn}
                                        onClick={() => setConfirming(false)}
                                        disabled={analogsStarting}
                                    >
                                        Нет
                                    </button>
                                </span>
                            ) : (
                                <Tooltip text={analogToggleTitle} align="left">
                                    <span className={cx(styles.analogToggle, styles[`analogToggle_${analogZone}`])}>
                                        <Switch
                                            label="Логистика"
                                            checked={analogZone === 'on' || analogZone === 'calc'}
                                            disabled={analogZone === 'calc' || (!analogsPriced && analogsLogistics !== 'done')}
                                            onChange={toggleAnalogsLogistics}
                                        />
                                        <span className={styles.analogToggleState}>{analogToggleState}</span>
                                    </span>
                                </Tooltip>
                            )
                        )}

                        {position.analogs && analogs && !analogsSearching && (
                            <span className={styles.note}>
                                {analogOffers.length ? `${analogOffers.length} аналогов` : 'аналогов нет'}
                                {analogAvg ? ` · средняя за единицу ${formatRub(analogAvg)}` : ''}
                            </span>
                        )}
                    </header>

                    {!position.analogs || analogsLogistics === 'skipped' ? (
                        <p className={styles.empty}>Поиск аналогов выключен для этой позиции.</p>
                    ) : analogsFailed ? (
                        <p className={styles.empty}>Не удалось загрузить аналоги.</p>
                    ) : searching || !analogs || analogsSearching ? (
                        <Waiting
                            text={analogsSlow ? 'Подбираем аналоги… это занимает дольше обычного' : 'Подбираем аналоги…'}
                        />
                    ) : !analogs.requested || analogs.skipped ? (
                        <p className={styles.empty}>Поиск аналогов выключен для этой позиции.</p>
                    ) : analogOffers.length ? (
                        <OffersTable
                            offers={analogOffers}
                            qty={position.qty}
                            badge=""
                            withMatch
                            zone={analogZone}
                            calcSpinner
                            deliveryOf={(offer) => offer.deliveryCost ?? 0}
                        />
                    ) : (
                        <div className={styles.emptyBox}>
                            <p className={styles.empty}>Подходящих аналогов не нашлось.</p>
                        </div>
                    )}
                </section>

                {/* ═══ логистика: две строки ═══ */}
                <section className={styles.blk}>
                    <header className={position.logistics || analogZone === 'on' ? styles.logi : styles.off}>
                        <span className={styles.cap}>Логистика</span>
                        <span className={styles.note}>средняя стоимость с доставкой</span>
                    </header>

                    <div className={styles.logiRow}>
                        <span className={styles.logiKey}>Основной товар</span>
                        {mainZone === 'calc' ? (
                            <Waiting text="Считаем доставку…" />
                        ) : mainZone === 'on' ? (
                            <span>
                                Средняя стоимость с учётом доставки{' '}
                                <b className={styles.logiBest}>{formatRub(goodsSum + deliverySum)}</b>
                            </span>
                        ) : (
                            <span className={styles.faint}>
                                Расчёт логистики не запускался.
                                {mainZone === 'pending' && !address && ' Укажите адрес доставки.'}
                                {mainZone === 'off' && ' Включите тумблер «Логистика» в строке позиции.'}
                            </span>
                        )}
                    </div>

                    {showAnalogsBlock && (
                        <div className={styles.logiRow}>
                            <span className={styles.logiKey}>Аналоги</span>
                            {analogZone === 'calc' ? (
                                <Waiting text="Считаем доставку по аналогам…" />
                            ) : analogZone === 'on' ? (
                                analogAvgWithDelivery ? (
                                    <span>
                                        Средняя стоимость с учётом доставки{' '}
                                        <b className={styles.logiBest}>{formatRub(analogAvgWithDelivery)}</b>
                                    </span>
                                ) : (
                                    <span className={styles.faint}>Доставку по аналогам посчитать не удалось.</span>
                                )
                            ) : (
                                <span className={styles.faint}>
                                    Расчёт логистики не запускался.
                                    {analogZone === 'pending' && ' Включите тумблер «Логистика» в шапке блока «Аналоги».'}
                                </span>
                            )}
                        </div>
                    )}
                </section>
            </div>

            <div className={styles.right}>
                <section className={styles.blk}>
                    <header className={styles.ref}>
                        <span className={styles.cap}>Справочные цены</span>
                    </header>

                    {marketLoading ? (
                        <Waiting text="Запрашиваем площадки…" />
                    ) : wbRemoved && portalRemoved ? (
                        <p className={styles.empty}>Справочные цены удалены.</p>
                    ) : (
                        <div className={styles.refList}>
                            {!portalRemoved && (
                                <RefCard
                                    logo="pp"
                                    name={REF_NAMES.portal}
                                    productName={portal?.item?.name || position.name}
                                    qty={position.qty}
                                    price={portal?.item?.price ?? null}
                                    url={portal?.item?.url}
                                    emptyText={portalFailed ? 'Портал не ответил' : 'Цена отсутствует на площадке'}
                                    onDelete={canDelete && !portalFailed ? () => askDeleteRef('portal') : undefined}
                                />
                            )}

                            {!wbRemoved && (
                                <RefCard
                                    logo="wb"
                                    name={REF_NAMES.wb}
                                    productName={wildberries?.title || position.name}
                                    qty={position.qty}
                                    price={wildberries?.price ?? null}
                                    url={wildberries?.url}
                                    emptyText={marketFailed ? 'Площадка не ответила' : 'Цена отсутствует на площадке'}
                                    onDelete={canDelete && !marketFailed ? () => askDeleteRef('wb') : undefined}
                                />
                            )}
                        </div>
                    )}
                </section>
            </div>

            {!searching && (offers.length > 0 || deliverySum > 0) && (
                <div className={styles.total}>
                    <span className={styles.totalCap}>Итого по позиции:</span>
                    <span className={styles.totalParts}>
                        {goodsSum ? `товары ${formatRub(goodsSum)} ` : 'товары не найдены'}
                        {zoneVisible(mainZone) && (
                            <span className={styles.totalPart}>
                                доставка <b>{mainZone === 'on' ? formatRub(deliverySum) : '—'}</b>
                            </span>
                        )}
                        {zoneVisible(analogZone) && (
                            <span className={styles.totalPart}>
                                аналоги с доставкой{' '}
                                <b>{analogZone === 'on' && analogAvgWithDelivery ? formatRub(analogAvgWithDelivery) : '—'}</b>
                            </span>
                        )}
                    </span>
                    <span className={styles.totalSum}>{formatRub(goodsSum + (mainZone === 'on' ? deliverySum : 0))}</span>
                </div>
            )}

            {deleteTarget && (
                <ConfirmDeleteModal
                    open
                    {...deleteCopy(deleteTarget)}
                    loading={deleting}
                    onCancel={cancelDelete}
                    onConfirm={() => void confirmDelete()}
                />
            )}
        </div>
    );
};